'use client';

import { createLogger } from '@ctrl-enter/tools/utils';
import {
  Activity,
  ChevronDown,
  Clock,
  Crown,
  Eye,
  FileText,
  Inbox,
  Link2,
  Loader2,
  Mail,
  MessageSquare,
  Mic,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Search,
  Send,
  ShieldAlert,
  ShieldCheck,
  Trash2,
  TrendingUp,
  UserCheck,
  UserMinus,
  Users,
  X,
  Zap,
} from 'lucide-react';
import * as React from 'react';
import { useAuth } from '../auth';
import {
  useAdminRemoveMemberMutation,
  useAdminResendInvitationMutation,
  useAdminRevokeInvitationMutation,
  useAdminTeamMembersQuery,
  useAdminUpdateMemberRoleMutation,
  useAdminUsageQuery,
  useAdminUsageTimeseriesQuery,
} from '../hooks/use-gateway-queries';
import { useSeatLimit } from '../hooks/use-seat-limit';
import { cn } from '../utils/cn';
import { toast } from '../utils/toast';
import { getUserColor } from '../utils/chart-theme';
import { Button } from './button';
import { InfoSection } from './info-section';
import { InviteTeamMemberModal } from './invite-team-member-modal';
import { OrgUsageChart } from './org-usage-chart';
import { UserDetailModal } from './user-detail-modal';

const logger = createLogger('web:component:admin-panel');

type AdminTab = 'team' | 'usage';

// ─── Small helpers ────────────────────────────────────────────────────────────

function getInitials(firstName?: string, lastName?: string, email?: string): string {
  if (firstName && lastName) return `${firstName[0]}${lastName[0]}`.toUpperCase();
  if (firstName) return firstName.slice(0, 2).toUpperCase();
  if (email) return email.slice(0, 2).toUpperCase();
  return '??';
}

export function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

const DAY_MS = 86_400_000;

/** Short relative time ("today", "3d ago", "2w ago"). Returns "—" when missing/invalid. */
export function formatRelative(iso?: string): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const diff = Date.now() - then;
  if (diff < 0) return 'just now';
  if (diff < DAY_MS) return 'today';
  const days = Math.floor(diff / DAY_MS);
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

/** Per-user usage record as returned by the admin usage query. */
type UserUsage = {
  userId: string;
  messages: number;
  sessions: number;
  files: number;
  lastActive?: string;
};

/** Build a userId → usage lookup from the usage query response. */
function buildUsageMap(users: UserUsage[] | undefined): Map<string, UserUsage> {
  return new Map((users ?? []).map(u => [u.userId, u]));
}

type ActivityStatus = 'active-today' | 'active-month' | 'inactive' | 'no-usage';

function usageDisplayName(u: { firstName?: string; lastName?: string; email: string }): string {
  if (u.firstName && u.lastName) return `${u.firstName} ${u.lastName}`;
  if (u.firstName) return u.firstName;
  return u.email.split('@')[0] || u.email;
}

/** Classify a member's product activity for badges and insight cards. */
function getActivityStatus(usage?: UserUsage, memberStatus?: string): ActivityStatus {
  if (memberStatus === 'inactive' || memberStatus === 'pending') return 'inactive';
  const hasUsage = !!usage && (usage.messages > 0 || usage.sessions > 0 || usage.files > 0);
  if (!usage?.lastActive && !hasUsage) return 'no-usage';
  if (usage?.lastActive) {
    const diff = Date.now() - new Date(usage.lastActive).getTime();
    if (diff < DAY_MS) return 'active-today';
    if (diff <= 30 * DAY_MS) return 'active-month';
    return 'inactive';
  }
  return hasUsage ? 'active-month' : 'no-usage';
}

const ACTIVITY_STATUS_META: Record<ActivityStatus, { label: string; tone: BadgeTone }> = {
  'active-today': { label: 'Active today', tone: 'primary' },
  'active-month': { label: 'Active', tone: 'neutral' },
  inactive: { label: 'Inactive', tone: 'warning' },
  'no-usage': { label: 'No usage', tone: 'neutral' },
};

const ActivityStatusBadge = ({
  usage,
  memberStatus,
}: {
  usage?: UserUsage;
  memberStatus?: string;
}) => {
  const status = getActivityStatus(usage, memberStatus);
  const meta = ACTIVITY_STATUS_META[status];
  return <Badge tone={meta.tone}>{meta.label}</Badge>;
};

function countActiveThisMonth(usageUsers: UserUsage[]): number {
  const anyLastActive = usageUsers.some(u => !!u.lastActive);
  if (anyLastActive) {
    return usageUsers.filter(
      u => u.lastActive && Date.now() - new Date(u.lastActive).getTime() <= 30 * DAY_MS
    ).length;
  }
  return usageUsers.filter(u => u.messages > 0).length;
}

// ─── Presentational primitives ──────────────────────────────────────────────────
// Shared building blocks for the "Organization Control Center" dashboard.

type BadgeTone = 'primary' | 'neutral' | 'warning';

const BADGE_TONES: Record<BadgeTone, string> = {
  primary: 'bg-primary/10 text-primary',
  neutral: 'bg-muted/60 text-muted-foreground',
  warning: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
};

/** Unified pill badge used for roles, statuses, and the "You" marker. */
export const Badge = ({
  tone = 'neutral',
  icon,
  children,
}: {
  tone?: BadgeTone;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <span
    className={cn(
      'inline-flex items-center gap-1 text-[10px] leading-none px-2 py-0.5 rounded-full font-medium whitespace-nowrap',
      BADGE_TONES[tone]
    )}
  >
    {icon}
    {children}
  </span>
);

/** Tailwind class for a numeric cell — muted when the value is zero. */
const numTone = (n: number) => (n > 0 ? 'text-foreground' : 'text-muted-foreground/40');

/** A single shimmering placeholder row, matching the member/invitation row shape. */
const RowSkeleton = () => (
  <div className="flex items-center gap-3 py-3">
    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-muted/40 animate-pulse" />
    <div className="flex-1 min-w-0 space-y-1.5">
      <div className="h-3 w-[40%] rounded bg-muted/40 animate-pulse" />
      <div className="h-2.5 w-[60%] rounded bg-muted/30 animate-pulse" />
    </div>
    <div className="hidden sm:block h-4 w-14 rounded-full bg-muted/30 animate-pulse" />
  </div>
);

/** A stack of skeleton rows for loading lists (keeps card height stable). */
const RowsSkeleton = ({ rows = 3 }: { rows?: number }) => (
  <div className="divide-y divide-border/40">
    {Array.from({ length: rows }).map((_, i) => (
      <RowSkeleton key={i} />
    ))}
  </div>
);

type StatCardProps = {
  label: string;
  value: React.ReactNode;
  icon: React.ReactNode;
  /** Small caption under the value (e.g. "of 25 seats"). Optional. */
  hint?: React.ReactNode;
  loading?: boolean;
  /** When provided, renders a thin usage bar under the value. */
  progress?: { used: number; total: number };
  /** Optional status chip beside the label. */
  chip?: React.ReactNode;
};

/** Compact metric card used in the org summary row and usage overview. */
export const StatCard = ({ label, value, icon, hint, loading, progress, chip }: StatCardProps) => {
  const overCapacity = progress ? progress.used > progress.total : false;
  const pct =
    progress && progress.total > 0 ? Math.min((progress.used / progress.total) * 100, 100) : 0;

  return (
    <div className="flex flex-col gap-2 rounded-xl border border-border/50 bg-bg-neutral-8 px-4 py-3 transition-colors hover:border-border">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-muted-foreground/80 min-w-0">
          {icon}
          <span className="text-[10px] font-medium uppercase tracking-wide truncate">{label}</span>
        </div>
        {chip}
      </div>
      {loading ? (
        <div className="h-7 w-14 rounded-md bg-muted/40 animate-pulse" />
      ) : (
        <span className="text-[26px] font-semibold text-foreground tabular-nums leading-none">
          {value}
        </span>
      )}
      {progress && progress.total > 0 && !loading && (
        <div className="flex flex-col gap-1">
          <div className="w-full h-1.5 bg-border/40 rounded-full overflow-hidden">
            <div
              className={cn(
                'h-full rounded-full transition-all duration-500',
                overCapacity ? 'bg-destructive/70' : 'bg-primary/70'
              )}
              style={{ width: `${pct}%` }}
            />
          </div>
          <span
            className={cn(
              'text-[10px]',
              overCapacity ? 'text-destructive/80' : 'text-muted-foreground/60'
            )}
          >
            {overCapacity ? 'Over seat limit' : `${Math.round(pct)}% of seats used`}
          </span>
        </div>
      )}
      {hint && <span className="text-[10px] text-muted-foreground/60">{hint}</span>}
    </div>
  );
};

/**
 * Compact usage chip shown on member rows (messages / sessions / last active).
 * Pass `value` for real data; omit it to render a muted "—" placeholder.
 */
const UsageChip = ({
  icon,
  title,
  value,
}: {
  icon: React.ReactNode;
  title: string;
  value?: React.ReactNode;
}) => (
  <span
    title={title}
    className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md bg-muted/40 text-muted-foreground/70 tabular-nums select-none"
  >
    {icon}
    {value ?? '—'}
  </span>
);

type InsightTileProps = {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  icon: React.ReactNode;
  loading?: boolean;
  accent?: 'default' | 'primary' | 'warning';
};

/** Small insight cell used in org health and usage insight strips. */
const InsightTile = ({
  label,
  value,
  sub,
  icon,
  loading,
  accent = 'default',
}: InsightTileProps) => (
  <div
    className={cn(
      'flex flex-col gap-1.5 rounded-lg border px-3 py-2.5 min-h-[72px]',
      accent === 'primary' && 'border-primary/25 bg-primary/5',
      accent === 'warning' && 'border-amber-500/20 bg-amber-500/5',
      accent === 'default' && 'border-border/40 bg-muted/20'
    )}
  >
    <div className="flex items-center gap-1.5 text-muted-foreground/80">
      {icon}
      <span className="text-[10px] font-medium uppercase tracking-wide leading-tight">{label}</span>
    </div>
    {loading ? (
      <div className="h-5 w-10 rounded bg-muted/40 animate-pulse" />
    ) : (
      <span className="text-base font-semibold text-foreground tabular-nums leading-none">
        {value}
      </span>
    )}
    {sub && !loading && (
      <span className="text-[10px] text-muted-foreground/70 leading-snug">{sub}</span>
    )}
  </div>
);

type EmptyStateProps = {
  icon: React.ReactNode;
  title: string;
  hint?: string;
  action?: React.ReactNode;
};

/** Intentional, non-error empty state for cards and sections. */
export const EmptyState = ({ icon, title, hint, action }: EmptyStateProps) => (
  <div className="flex flex-col items-center justify-center text-center py-8 gap-2">
    <div className="text-muted-foreground/40">{icon}</div>
    <p className="text-sm text-muted-foreground">{title}</p>
    {hint && <p className="text-xs text-muted-foreground/60 max-w-xs">{hint}</p>}
    {action && <div className="pt-1">{action}</div>}
  </div>
);

/**
 * Tiny inline sparkline for per-user usage rows in the activity table.
 * Data is pivoted from the timeseries query for a given userId + metric.
 * When no data is available, renders a muted placeholder line.
 */
const UserSparkline = ({
  userId,
  metric,
  color,
}: {
  userId: string;
  metric: 'messages' | 'sessions' | 'files';
  color: string;
}) => {
  const { data } = useAdminUsageTimeseriesQuery(30);
  const series = data?.status === 200 ? data.body.series : undefined;

  const values = React.useMemo(() => {
    if (!series) return [];
    return series.map(day => {
      const u = day.perUser.find(p => p.userId === userId);
      return u ? u[metric] : 0;
    });
  }, [series, userId, metric]);

  const max = Math.max(...values, 1);
  const W = 48;
  const H = 20;

  if (!values.length || values.every(v => v === 0)) {
    return (
      <svg width={W} height={H} className="opacity-20">
        <line x1={0} y1={H / 2} x2={W} y2={H / 2} stroke="currentColor" strokeWidth={1} />
      </svg>
    );
  }

  const step = W / (values.length - 1 || 1);
  const points = values.map((v, i) => `${i * step},${H - (v / max) * (H - 2) - 1}`).join(' ');

  return (
    <svg width={W} height={H} className="overflow-visible">
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

// ─── Organization summary row ───────────────────────────────────────────────────

/**
 * Top-of-dashboard summary strip. Wires real/derived values from the team,
 * usage, and subscription queries.
 */
const OrgSummaryRow = () => {
  const { user } = useAuth();
  const { data: teamData, isLoading: teamLoading } = useAdminTeamMembersQuery(true);
  const { data: usageData, isLoading: usageLoading } = useAdminUsageQuery(true);

  const members = teamData?.body?.members ?? [];
  const invitations = teamData?.body?.invitations ?? [];

  const memberCount = members.length;
  const pendingCount = invitations.length;

  // Seats consumed = active members + outstanding invitations. This mirrors the
  // seat-count logic the server reports to the Stripe meter on member changes.
  const activeMembers = members.filter(m => m.status === 'active').length;
  const seatsUsed = activeMembers + pendingCount;

  // Purchased seat limit: manual_seat_count for manual orgs, else the Stripe
  // subscription quantity. When present we render "used / total" with a progress bar.
  const seatLimit = useSeatLimit();

  // "Active this month" = users active within the last 30 days based on their
  // `lastActive` timestamp. If no timestamps exist yet (older data predating
  // tracking), fall back to "has sent at least one message" so the figure isn't
  // misleadingly zero.
  const usageUsers = usageData?.status === 200 ? usageData.body.users : [];
  const activeThisMonth = countActiveThisMonth(usageUsers);
  const adoptionPct = activeMembers > 0 ? Math.round((activeThisMonth / activeMembers) * 100) : 0;

  const seatsCard: StatCardProps = {
    label: 'Seats used',
    value: seatLimit ? `${seatsUsed} / ${seatLimit}` : seatsUsed,
    icon: <ShieldCheck size={14} />,
    loading: teamLoading,
    ...(seatLimit
      ? {
          progress: { used: seatsUsed, total: seatLimit },
          chip:
            seatsUsed >= seatLimit ? (
              <Badge tone="warning">At limit</Badge>
            ) : seatsUsed > 0 ? (
              <Badge tone="primary">{Math.round((seatsUsed / seatLimit) * 100)}%</Badge>
            ) : undefined,
        }
      : { hint: 'active + pending' }),
  };

  const stats: StatCardProps[] = [
    {
      label: 'Members',
      value: memberCount,
      icon: <Users size={14} />,
      loading: teamLoading,
      chip: memberCount === 1 ? <Badge tone="neutral">Solo</Badge> : undefined,
    },
    {
      label: 'Pending invites',
      value: pendingCount,
      icon: <Mail size={14} />,
      loading: teamLoading,
      chip:
        pendingCount === 0 && !teamLoading ? (
          <Badge tone="primary">All accepted</Badge>
        ) : pendingCount > 0 ? (
          <Badge tone="warning">Awaiting</Badge>
        ) : undefined,
    },
    seatsCard,
    {
      label: 'Active this month',
      value: activeThisMonth,
      icon: <Activity size={14} />,
      loading: usageLoading,
      hint: activeMembers > 0 ? `${adoptionPct}% of active members` : undefined,
      chip:
        !usageLoading && activeMembers > 0 ? (
          <Badge tone={adoptionPct >= 50 ? 'primary' : 'warning'}>{adoptionPct}%</Badge>
        ) : undefined,
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {stats.map(s => (
        <StatCard key={s.label} {...s} />
      ))}
    </div>
  );
};

/**
 * Org Health / Team Adoption — the primary admin signal strip summarizing
 * adoption, capacity, and standout members at a glance.
 */
const OrgHealthPanel = () => {
  const { user } = useAuth();
  const { data: teamData, isLoading: teamLoading } = useAdminTeamMembersQuery(true);
  const { data: usageData, isLoading: usageLoading } = useAdminUsageQuery(true);

  const loading = teamLoading || usageLoading;
  const members = teamData?.body?.members ?? [];
  const invitations = teamData?.body?.invitations ?? [];
  const usageUsers = usageData?.status === 200 ? usageData.body.users : [];
  const usageMap = buildUsageMap(usageUsers);

  const activeMembers = members.filter(m => m.status === 'active');
  const activeThisMonth = countActiveThisMonth(usageUsers);
  const inactiveCount = activeMembers.filter(m => {
    const status = getActivityStatus(usageMap.get(m.userId), m.status);
    return status === 'inactive' || status === 'no-usage';
  }).length;

  const seatsUsed = activeMembers.length + invitations.length;
  const seatLimit = useSeatLimit();

  const mostActive = [...usageUsers].sort(
    (a, b) => b.messages + b.sessions + b.files - (a.messages + a.sessions + a.files)
  )[0];
  const mostActiveScore = mostActive
    ? mostActive.messages + mostActive.sessions + mostActive.files
    : 0;

  const adoptionPct =
    activeMembers.length > 0 ? Math.round((activeThisMonth / activeMembers.length) * 100) : 0;

  const headline = loading
    ? 'Loading adoption insights…'
    : activeMembers.length === 0
      ? 'Invite teammates to start measuring adoption.'
      : adoptionPct >= 75
        ? `Strong adoption — ${activeThisMonth} of ${activeMembers.length} members active this month.`
        : adoptionPct >= 40
          ? `Moderate adoption — ${activeThisMonth} of ${activeMembers.length} members active this month.`
          : `Low adoption — only ${activeThisMonth} of ${activeMembers.length} members active this month.`;

  return (
    <div className="rounded-xl border border-border/50 bg-bg-neutral-8 px-4 py-4 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1 min-w-0">
          <div className="flex items-center gap-2">
            <Zap size={14} className="text-primary flex-shrink-0" />
            <p className="text-[10px] font-medium uppercase tracking-wide text-primary/90">
              Org health · Team adoption
            </p>
          </div>
          <p className="text-sm text-muted-foreground leading-snug">{headline}</p>
        </div>
        {!loading && seatLimit != null && (
          <div className="flex flex-col items-end gap-1.5 min-w-[120px]">
            <Badge tone={seatsUsed >= seatLimit ? 'warning' : 'primary'}>
              {seatsUsed} / {seatLimit} seats
            </Badge>
            <div className="w-full h-1.5 bg-border/40 rounded-full overflow-hidden">
              <div
                className={cn(
                  'h-full rounded-full transition-all',
                  seatsUsed >= seatLimit ? 'bg-destructive/70' : 'bg-primary/70'
                )}
                style={{ width: `${Math.min((seatsUsed / seatLimit) * 100, 100)}%` }}
              />
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        <InsightTile
          label="Active this month"
          value={activeThisMonth}
          sub={activeMembers.length > 0 ? `${adoptionPct}% adoption` : undefined}
          icon={<Activity size={12} />}
          loading={loading}
          accent="primary"
        />
        <InsightTile
          label="Inactive"
          value={inactiveCount}
          sub="No recent usage"
          icon={<UserMinus size={12} />}
          loading={loading}
          accent={inactiveCount > 0 ? 'warning' : 'default'}
        />
        <InsightTile
          label="Pending invites"
          value={invitations.length}
          sub={invitations.length === 0 ? 'Queue clear' : 'Awaiting acceptance'}
          icon={<Mail size={12} />}
          loading={teamLoading}
          accent={invitations.length > 0 ? 'warning' : 'default'}
        />
        <InsightTile
          label="Seat usage"
          value={seatLimit ? `${seatsUsed}/${seatLimit}` : seatsUsed}
          sub={
            seatLimit ? `${Math.round((seatsUsed / seatLimit) * 100)}% filled` : 'active + pending'
          }
          icon={<ShieldCheck size={12} />}
          loading={teamLoading}
        />
        <InsightTile
          label="Most active"
          value={mostActive && mostActiveScore > 0 ? usageDisplayName(mostActive) : '—'}
          sub={
            mostActive && mostActiveScore > 0
              ? `${mostActive.messages} msgs · ${mostActive.sessions} sessions`
              : 'No activity yet'
          }
          icon={<TrendingUp size={12} />}
          loading={usageLoading}
          accent={mostActiveScore > 0 ? 'primary' : 'default'}
        />
      </div>
    </div>
  );
};

// ─── Row action menu ──────────────────────────────────────────────────────────

type MemberActionsProps = {
  membershipId: string;
  userId: string;
  role: 'admin' | 'member';
  isSelf: boolean;
  /** Navigate to the Usage tab to inspect this member's activity. */
  onViewUsage?: () => void;
  /** Open the per-user detail modal for this member. */
  onSelectUser?: (userId: string) => void;
};

const MemberActions = ({
  membershipId,
  userId,
  role,
  isSelf,
  onViewUsage,
  onSelectUser,
}: MemberActionsProps) => {
  const [open, setOpen] = React.useState(false);
  const [confirming, setConfirming] = React.useState(false);
  const removeMutation = useAdminRemoveMemberMutation();
  const roleMutation = useAdminUpdateMemberRoleMutation();
  const menuRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleRemove = async () => {
    try {
      await removeMutation.mutateAsync({ params: { membershipId }, body: {} });
    } catch (err) {
      logger.error('[AdminPanel] Remove failed:', err);
    }
    setConfirming(false);
    setOpen(false);
  };

  const handleToggleRole = async () => {
    const newRole = role === 'admin' ? 'member' : 'admin';
    try {
      await roleMutation.mutateAsync({ params: { membershipId }, body: { role: newRole } });
    } catch (err) {
      logger.error('[AdminPanel] Role update failed:', err);
    }
    setOpen(false);
  };

  if (isSelf) return null;

  return (
    <div ref={menuRef} className="relative">
      <button
        type="button"
        onClick={e => {
          e.stopPropagation();
          setOpen(v => !v);
        }}
        aria-label="Member actions"
        className={cn(
          'p-1.5 rounded-lg text-muted-foreground transition-colors hover:text-foreground hover:bg-muted/60',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
          open && 'bg-muted/60 text-foreground'
        )}
      >
        <MoreHorizontal size={14} />
      </button>

      {open && (
        <div className="absolute right-0 mt-1 w-44 rounded-xl border border-border bg-bg-neutral-8 shadow-lg z-10 overflow-hidden p-1">
          {confirming ? (
            <div className="p-2 space-y-2">
              <p className="text-xs text-foreground px-1 pt-1">Remove this member?</p>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="flex-1 text-xs px-2 py-1.5 rounded-lg border border-border text-muted-foreground transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
                  onClick={() => {
                    setConfirming(false);
                    setOpen(false);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="flex-1 text-xs px-2 py-1.5 rounded-lg bg-destructive text-destructive-foreground transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive/40 disabled:opacity-50"
                  onClick={handleRemove}
                  disabled={removeMutation.isPending}
                >
                  {removeMutation.isPending ? 'Removing…' : 'Remove'}
                </button>
              </div>
            </div>
          ) : (
            <>
              {onViewUsage && (
                <button
                  type="button"
                  onClick={() => {
                    if (onSelectUser) {
                      onSelectUser(userId);
                    } else {
                      onViewUsage();
                    }
                    setOpen(false);
                  }}
                  className="flex w-full items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs text-foreground transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:bg-muted/60"
                >
                  <Eye size={12} className="text-muted-foreground" />
                  View usage
                </button>
              )}
              <button
                type="button"
                onClick={handleToggleRole}
                disabled={roleMutation.isPending}
                className="flex w-full items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs text-foreground transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:bg-muted/60 disabled:opacity-50"
              >
                <Crown size={12} className="text-muted-foreground" />
                {role === 'admin' ? 'Demote to Member' : 'Promote to Admin'}
              </button>
              <div className="my-1 -mx-1 border-t border-border/60" />
              <button
                type="button"
                onClick={() => setConfirming(true)}
                className="flex w-full items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs text-destructive transition-colors hover:bg-destructive/10 focus-visible:outline-none focus-visible:bg-destructive/10"
              >
                <Trash2 size={12} />
                Remove from org
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Team tab ─────────────────────────────────────────────────────────────────

type RoleFilter = 'all' | 'admin' | 'member';
type StatusFilter = 'all' | 'active' | 'pending' | 'inactive';

const TeamTab = ({
  onViewUsage,
  onSelectUser,
}: {
  onViewUsage?: () => void;
  onSelectUser?: (userId: string) => void;
}) => {
  const { user } = useAuth();
  const [showInvite, setShowInvite] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [roleFilter, setRoleFilter] = React.useState<RoleFilter>('all');
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>('all');

  const { data, isLoading, isError, refetch } = useAdminTeamMembersQuery(true);
  const { data: usageResp } = useAdminUsageQuery(true);
  const revokeInviteMutation = useAdminRevokeInvitationMutation();
  const resendInviteMutation = useAdminResendInvitationMutation();
  const [resendingId, setResendingId] = React.useState<string | null>(null);

  const members = data?.body?.members ?? [];
  const invitations = data?.body?.invitations ?? [];
  const onlySelf = !isLoading && !isError && members.length <= 1;

  // Join per-member usage from the usage query (keyed by userId).
  const usageByUserId = buildUsageMap(usageResp?.status === 200 ? usageResp.body.users : undefined);

  // Client-side filtering over already-fetched members (no extra backend calls).
  const query = search.trim().toLowerCase();
  const hasActiveFilters = query !== '' || roleFilter !== 'all' || statusFilter !== 'all';
  const filteredMembers = members.filter(m => {
    if (roleFilter !== 'all' && m.role !== roleFilter) return false;
    if (statusFilter !== 'all' && m.status !== statusFilter) return false;
    if (query) {
      const name = `${m.firstName ?? ''} ${m.lastName ?? ''}`.toLowerCase();
      if (!name.includes(query) && !m.email.toLowerCase().includes(query)) return false;
    }
    return true;
  });

  const clearFilters = () => {
    setSearch('');
    setRoleFilter('all');
    setStatusFilter('all');
  };

  const handleRevokeInvite = async (invitationId: string) => {
    try {
      await revokeInviteMutation.mutateAsync({ params: { invitationId }, body: {} });
    } catch (err) {
      logger.error('[AdminPanel] Revoke invite failed:', err);
    }
  };

  const handleResendInvite = async (invitationId: string, email: string) => {
    setResendingId(invitationId);
    try {
      await resendInviteMutation.mutateAsync({ params: { invitationId }, body: { email } });
      toast.success('Invitation resent', `A fresh invite was sent to ${email}`);
    } catch (err) {
      logger.error('[AdminPanel] Resend invite failed:', err);
      toast.error('Resend failed', 'Could not resend the invitation. Please try again.');
    } finally {
      setResendingId(null);
    }
  };

  const handleCopyInviteLink = async (acceptInvitationUrl?: string) => {
    if (!acceptInvitationUrl) {
      toast.error('No invite link', 'This invitation has no shareable link available.');
      return;
    }
    try {
      await navigator.clipboard.writeText(acceptInvitationUrl);
      toast.success('Link copied', 'Invite link copied to your clipboard.');
    } catch (err) {
      logger.error('[AdminPanel] Copy invite link failed:', err);
      toast.error('Copy failed', 'Could not copy the invite link.');
    }
  };

  return (
    <div className="space-y-5">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">
            {isLoading ? 'Loading…' : `${members.length} member${members.length !== 1 ? 's' : ''}`}
            {invitations.length > 0 && `, ${invitations.length} pending`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => refetch()}
            className="p-1.5 rounded-lg text-muted-foreground transition-colors hover:text-foreground hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
            title="Refresh"
          >
            <RefreshCw size={13} />
          </button>
          <Button variant="primary" size="sm" onClick={() => setShowInvite(true)}>
            <Plus size={13} className="mr-1.5" />
            Invite member
          </Button>
        </div>
      </div>

      {/* Error */}
      {isError && (
        <InfoSection>
          <div className="text-center py-6 space-y-3">
            <p className="text-sm text-destructive">Failed to load team members.</p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Try again
            </Button>
          </div>
        </InfoSection>
      )}

      {/* Members card */}
      {!isError && (
        <InfoSection title="Members" titleIcon={<Users size={16} />}>
          {isLoading ? (
            <RowsSkeleton rows={3} />
          ) : members.length > 0 ? (
            <>
              {/* Search + filters */}
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[160px]">
                  <Search
                    size={13}
                    className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground/60 pointer-events-none"
                  />
                  <input
                    type="text"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search members…"
                    className="w-full pl-8 pr-3 py-1.5 rounded-lg text-xs bg-bg-neutral-8 border border-border text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground/50"
                  />
                </div>
                <div className="relative">
                  <select
                    value={roleFilter}
                    onChange={e => setRoleFilter(e.target.value as RoleFilter)}
                    className="appearance-none pl-3 pr-7 py-1.5 rounded-lg text-xs bg-bg-neutral-8 border border-border text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                  >
                    <option value="all">All roles</option>
                    <option value="admin">Admins</option>
                    <option value="member">Members</option>
                  </select>
                  <ChevronDown
                    size={11}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
                  />
                </div>
                <div className="relative">
                  <select
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value as StatusFilter)}
                    className="appearance-none pl-3 pr-7 py-1.5 rounded-lg text-xs bg-bg-neutral-8 border border-border text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                  >
                    <option value="all">All statuses</option>
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="inactive">Inactive</option>
                  </select>
                  <ChevronDown
                    size={11}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
                  />
                </div>
              </div>

              {filteredMembers.length > 0 ? (
                <div className="divide-y divide-border/50 -mx-2">
                  {filteredMembers.map(member => {
                    const isSelf = member.userId === user?.id;
                    const usage = usageByUserId.get(member.userId);
                    const displayName =
                      member.firstName && member.lastName
                        ? `${member.firstName} ${member.lastName}`
                        : (member.firstName ?? member.email);

                    return (
                      <div
                        key={member.membershipId}
                        className="group flex items-center gap-3 py-3 px-2 rounded-lg transition-colors hover:bg-muted/30 cursor-pointer"
                        onClick={() => onSelectUser?.(member.userId)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={e => e.key === 'Enter' && onSelectUser?.(member.userId)}
                      >
                        {/* Avatar */}
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold select-none overflow-hidden">
                          {member.profilePictureUrl ? (
                            <img
                              src={member.profilePictureUrl}
                              alt=""
                              className="w-8 h-8 rounded-full object-cover"
                            />
                          ) : (
                            getInitials(member.firstName, member.lastName, member.email)
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span
                              className="text-sm font-medium text-foreground truncate"
                              title={displayName}
                            >
                              {displayName}
                            </span>
                            {isSelf && <Badge tone="neutral">You</Badge>}
                            {member.status === 'active' && (
                              <ActivityStatusBadge usage={usage} memberStatus={member.status} />
                            )}
                          </div>
                          <p
                            className="text-xs text-muted-foreground truncate"
                            title={member.email}
                          >
                            {member.email}
                          </p>
                          <div className="flex flex-wrap items-center gap-1.5 mt-1 lg:hidden">
                            <UsageChip
                              icon={<Clock size={10} />}
                              title="Last active"
                              value={formatRelative(usage?.lastActive)}
                            />
                            <UsageChip
                              icon={<MessageSquare size={10} />}
                              title="Messages"
                              value={(usage?.messages ?? 0).toLocaleString()}
                            />
                            <UsageChip
                              icon={<Activity size={10} />}
                              title="Sessions"
                              value={(usage?.sessions ?? 0).toLocaleString()}
                            />
                          </div>
                        </div>

                        {/* Usage chips — real per-member metrics joined from the usage query */}
                        <div className="hidden lg:flex flex-shrink-0 items-center gap-1.5">
                          <UsageChip
                            icon={<Clock size={10} />}
                            title="Last active"
                            value={formatRelative(usage?.lastActive)}
                          />
                          <UsageChip
                            icon={<MessageSquare size={10} />}
                            title="Messages"
                            value={(usage?.messages ?? 0).toLocaleString()}
                          />
                          <UsageChip
                            icon={<Activity size={10} />}
                            title="Sessions"
                            value={(usage?.sessions ?? 0).toLocaleString()}
                          />
                        </div>

                        {/* Role + status badges */}
                        <div className="flex-shrink-0 hidden sm:flex items-center gap-1.5">
                          {member.status === 'pending' && <Badge tone="warning">Pending</Badge>}
                          {member.status === 'inactive' && <Badge tone="neutral">Inactive</Badge>}
                          {member.role === 'admin' ? (
                            <Badge tone="primary" icon={<Crown size={9} />}>
                              Admin
                            </Badge>
                          ) : (
                            <Badge tone="neutral">Member</Badge>
                          )}
                        </div>

                        {/* Joined date */}
                        <div
                          className="flex-shrink-0 hidden md:block text-xs text-muted-foreground whitespace-nowrap"
                          title={`Joined ${formatDate(member.joinedAt)}`}
                        >
                          {formatDate(member.joinedAt)}
                        </div>

                        {/* Actions */}
                        <div className="flex-shrink-0">
                          <MemberActions
                            membershipId={member.membershipId}
                            userId={member.userId}
                            role={member.role}
                            isSelf={isSelf}
                            {...(onViewUsage ? { onViewUsage } : {})}
                            {...(onSelectUser ? { onSelectUser } : {})}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <EmptyState
                  icon={<Search size={24} />}
                  title="No members match your filters"
                  hint="Try a different search term or clear the filters."
                  action={
                    <Button variant="outline" size="sm" onClick={clearFilters}>
                      Clear filters
                    </Button>
                  }
                />
              )}
            </>
          ) : (
            <EmptyState
              icon={<UserCheck size={28} />}
              title="No team members found"
              hint="Invite colleagues to collaborate in your organization."
              action={
                <Button variant="primary" size="sm" onClick={() => setShowInvite(true)}>
                  <Plus size={13} className="mr-1.5" />
                  Invite first member
                </Button>
              }
            />
          )}

          {/* Gentle nudge when the admin is the only member */}
          {onlySelf && members.length === 1 && !hasActiveFilters && (
            <p className="text-xs text-muted-foreground/60 text-center pt-1">
              You're the only member so far — invite teammates to get started.
            </p>
          )}
        </InfoSection>
      )}

      {/* Pending invitations card — always rendered with an intentional empty state */}
      {!isError && (
        <InfoSection title="Pending invitations" titleIcon={<Mail size={16} />}>
          {isLoading ? (
            <RowsSkeleton rows={2} />
          ) : invitations.length > 0 ? (
            <div className="divide-y divide-border/50 -mx-2">
              {invitations.map(inv => (
                <div
                  key={inv.invitationId}
                  className="group flex items-center gap-3 py-3 px-2 rounded-lg transition-colors hover:bg-muted/30"
                >
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-muted/60 text-muted-foreground flex items-center justify-center text-xs font-semibold select-none">
                    {inv.email.slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground truncate" title={inv.email}>
                      {inv.email}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      Invited {formatDate(inv.sentAt)}
                      {inv.expiresAt && ` · expires ${formatDate(inv.expiresAt)}`}
                    </p>
                  </div>

                  {/* Status badge — reflects actual invitation state from WorkOS */}
                  <div className="flex-shrink-0">
                    {inv.state === 'expired' ? (
                      <Badge tone="neutral">Expired</Badge>
                    ) : inv.state === 'revoked' ? (
                      <Badge tone="neutral">Revoked</Badge>
                    ) : inv.state === 'accepted' ? (
                      <Badge tone="primary">Accepted</Badge>
                    ) : (
                      <Badge tone="warning">Pending</Badge>
                    )}
                  </div>

                  {/* Invitation actions: Resend, Copy link, and Cancel are all wired. */}
                  <div className="flex-shrink-0 flex items-center gap-1">
                    <button
                      type="button"
                      title="Resend invitation"
                      onClick={() => handleResendInvite(inv.invitationId, inv.email)}
                      disabled={resendingId === inv.invitationId}
                      className="p-1.5 rounded-lg text-muted-foreground transition-colors hover:text-foreground hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 disabled:opacity-50"
                    >
                      {resendingId === inv.invitationId ? (
                        <Loader2 size={13} className="animate-spin" />
                      ) : (
                        <Send size={13} />
                      )}
                    </button>
                    <button
                      type="button"
                      title={
                        inv.acceptInvitationUrl
                          ? 'Copy invite link'
                          : 'No invite link available for this invitation'
                      }
                      onClick={() => handleCopyInviteLink(inv.acceptInvitationUrl)}
                      disabled={!inv.acceptInvitationUrl}
                      className="p-1.5 rounded-lg text-muted-foreground transition-colors hover:text-foreground hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 disabled:text-muted-foreground/40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    >
                      <Link2 size={13} />
                    </button>
                    <button
                      type="button"
                      title="Cancel invitation"
                      onClick={() => handleRevokeInvite(inv.invitationId)}
                      disabled={revokeInviteMutation.isPending}
                      className="p-1.5 rounded-lg text-muted-foreground transition-colors hover:text-destructive hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive/30 disabled:opacity-50"
                    >
                      <X size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              icon={<Inbox size={26} />}
              title="No pending invitations"
              hint="Your invite queue is clear. Send a new invite when you're ready to grow the team."
              action={
                <Button variant="primary" size="sm" onClick={() => setShowInvite(true)}>
                  <Plus size={13} className="mr-1.5" />
                  Invite member
                </Button>
              }
            />
          )}
        </InfoSection>
      )}

      <InviteTeamMemberModal isOpen={showInvite} onClose={() => setShowInvite(false)} />
    </div>
  );
};

// ─── Usage tab ────────────────────────────────────────────────────────────────

/** Small donut-style composition bar (pure CSS, no recharts dep in this component). */
export const CompositionBar = ({
  items,
}: {
  items: { label: string; value: number; color: string }[];
}) => {
  const total = items.reduce((s, i) => s + i.value, 0);
  if (total === 0) return null;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex w-full h-1.5 rounded-full overflow-hidden gap-px">
        {items.map(item => (
          <div
            key={item.label}
            style={{ width: `${(item.value / total) * 100}%`, background: item.color }}
            title={`${item.label}: ${item.value}`}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-3">
        {items.map(item => (
          <div
            key={item.label}
            className="flex items-center gap-1 text-[10px] text-muted-foreground"
          >
            <span
              className="w-1.5 h-1.5 rounded-full flex-shrink-0"
              style={{ background: item.color }}
            />
            <span>
              {item.label}: <span className="text-foreground font-medium">{item.value}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const UsageTab = ({
  onGoToTeam,
  onSelectUser,
}: {
  onGoToTeam?: () => void;
  onSelectUser?: (userId: string) => void;
}) => {
  const { data, isLoading, isError, refetch } = useAdminUsageQuery(true);
  const { data: teamData } = useAdminTeamMembersQuery(true);
  const users = data?.body?.users ?? [];
  const totals = data?.body?.totals;

  const hasAnyActivity = !!totals && totals.messages + totals.sessions + totals.files > 0;
  const activeThisMonth = countActiveThisMonth(users);
  const inactiveUsers = users.filter(
    u => getActivityStatus(u) === 'inactive' || getActivityStatus(u) === 'no-usage'
  );
  const mostActiveUser = [...users].sort(
    (a, b) => b.messages + b.sessions + b.files - (a.messages + a.sessions + a.files)
  )[0];
  const mostActiveTotal = mostActiveUser
    ? mostActiveUser.messages + mostActiveUser.sessions + mostActiveUser.files
    : 0;

  // Org composition for the snapshot strip (from team data)
  const members = teamData?.body?.members ?? [];
  const compositionItems = React.useMemo(() => {
    const adminCount = members.filter(m => m.role === 'admin' && m.status === 'active').length;
    const memberCount = members.filter(m => m.role === 'member' && m.status === 'active').length;
    const pendingCount = members.filter(m => m.status === 'pending').length;
    const invitedCount = teamData?.body?.invitations?.length ?? 0;
    return [
      { label: 'Admin', value: adminCount, color: '#60a5fa' },
      { label: 'Member', value: memberCount, color: '#34d399' },
      { label: 'Pending', value: pendingCount + invitedCount, color: '#fbbf24' },
    ].filter(i => i.value > 0);
  }, [members, teamData]);

  // Activity recency buckets (from lastActive)
  const recencyBuckets = React.useMemo(() => {
    const DAY = 86_400_000;
    const now = Date.now();
    const counts = { today: 0, week: 0, month: 0, older: 0, never: 0 };
    for (const u of users) {
      if (!u.lastActive) {
        counts.never++;
        continue;
      }
      const diff = now - new Date(u.lastActive).getTime();
      if (diff < DAY) counts.today++;
      else if (diff < 7 * DAY) counts.week++;
      else if (diff < 30 * DAY) counts.month++;
      else counts.older++;
    }
    return counts;
  }, [users]);

  // Overview cards. `messages`, `sessions`, `files`, and `voiceSeconds` all come
  // from the usage query. Voice is stored as seconds and shown as rounded minutes.
  const voiceMinutes = Math.round((totals?.voiceSeconds ?? 0) / 60);
  const overviewCards: StatCardProps[] = [
    {
      label: 'Total messages',
      value: (totals?.messages ?? 0).toLocaleString(),
      icon: <MessageSquare size={14} />,
      loading: isLoading,
      chip:
        !isLoading && (totals?.messages ?? 0) === 0 ? (
          <Badge tone="neutral">No activity</Badge>
        ) : undefined,
    },
    {
      label: 'Total sessions',
      value: (totals?.sessions ?? 0).toLocaleString(),
      icon: <Activity size={14} />,
      loading: isLoading,
      chip:
        !isLoading && (totals?.sessions ?? 0) > 0 ? <Badge tone="primary">Live</Badge> : undefined,
    },
    {
      label: 'Files uploaded',
      value: (totals?.files ?? 0).toLocaleString(),
      icon: <FileText size={14} />,
      loading: isLoading,
    },
    {
      label: 'Voice minutes',
      value: voiceMinutes.toLocaleString(),
      icon: <Mic size={14} />,
      loading: isLoading,
      chip:
        !isLoading && voiceMinutes === 0 ? <Badge tone="neutral">No activity</Badge> : undefined,
    },
  ];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {isLoading ? 'Loading…' : `${users.length} ${users.length === 1 ? 'member' : 'members'}`}
        </p>
        <button
          type="button"
          onClick={() => refetch()}
          className="p-1.5 rounded-lg text-muted-foreground transition-colors hover:text-foreground hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
          title="Refresh"
        >
          <RefreshCw size={13} />
        </button>
      </div>

      {/* Error */}
      {isError && (
        <InfoSection>
          <div className="text-center py-6 space-y-3">
            <p className="text-sm text-destructive">Failed to load usage data.</p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Try again
            </Button>
          </div>
        </InfoSection>
      )}

      {/* Usage insight cards */}
      {!isError && !isLoading && users.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <InsightTile
            label="Most active member"
            value={mostActiveUser && mostActiveTotal > 0 ? usageDisplayName(mostActiveUser) : '—'}
            sub={
              mostActiveUser && mostActiveTotal > 0
                ? `${mostActiveUser.messages} messages · ${mostActiveUser.sessions} sessions`
                : 'Waiting for first activity'
            }
            icon={<TrendingUp size={12} />}
            accent={mostActiveTotal > 0 ? 'primary' : 'default'}
          />
          <InsightTile
            label="Inactive members"
            value={inactiveUsers.length}
            sub={
              inactiveUsers.length === 0
                ? 'Everyone has recent activity'
                : `${activeThisMonth} active this month`
            }
            icon={<UserMinus size={12} />}
            accent={inactiveUsers.length > 0 ? 'warning' : 'default'}
          />
        </div>
      )}

      {/* Org-wide time-series chart */}
      {!isError && (
        <InfoSection title="Usage over time" titleIcon={<Activity size={16} />}>
          {!isLoading && !hasAnyActivity && users.length > 0 && (
            <p className="text-xs text-muted-foreground/80 mb-3 px-0.5 border-l-2 border-primary/40 pl-2.5">
              No usage recorded yet — the chart below is ready and will populate as your team uses
              CTRL ENTER.
            </p>
          )}
          <OrgUsageChart />
        </InfoSection>
      )}

      {/* Usage overview cards */}
      {!isError && (
        <div className="space-y-2">
          <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground/70 px-0.5">
            Usage overview
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {overviewCards.map(card => (
              <StatCard key={card.label} {...card} />
            ))}
          </div>
        </div>
      )}

      {/* Snapshot strip: org composition + activity recency */}
      {!isError && !isLoading && members.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rounded-xl border border-border/50 bg-bg-neutral-8 px-4 py-3 space-y-2">
            <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground/70">
              Org composition
            </p>
            <CompositionBar items={compositionItems} />
          </div>
          <div className="rounded-xl border border-border/50 bg-bg-neutral-8 px-4 py-3 space-y-2">
            <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground/70">
              Activity recency
            </p>
            <div className="flex flex-wrap gap-2">
              {[
                { label: 'Today', value: recencyBuckets.today, color: '#34d399' },
                { label: 'This week', value: recencyBuckets.week, color: '#60a5fa' },
                { label: 'This month', value: recencyBuckets.month, color: '#a78bfa' },
                { label: 'Older', value: recencyBuckets.older, color: '#94a3b8' },
                { label: 'Never', value: recencyBuckets.never, color: '#475569' },
              ]
                .filter(b => b.value > 0)
                .map(b => (
                  <span
                    key={b.label}
                    className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-muted/40 text-muted-foreground"
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                      style={{ background: b.color }}
                    />
                    {b.label}: <span className="font-medium text-foreground">{b.value}</span>
                  </span>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Per-user activity section */}
      {!isError && (
        <InfoSection title="Per-user activity" titleIcon={<Users size={16} />}>
          {isLoading ? (
            <RowsSkeleton rows={3} />
          ) : users.length > 0 ? (
            <div className="space-y-3">
              {!hasAnyActivity && (
                <p className="text-xs text-muted-foreground/80 px-3 py-2 rounded-lg border border-border/40 bg-muted/20">
                  Team members are listed below. Activity badges and metrics will appear once people
                  start using the product.
                </p>
              )}
              <div className="overflow-x-auto">
                <div className="min-w-[720px]">
                  {/* Table header */}
                  <div className="grid grid-cols-[1.4fr_48px_auto_auto_auto_auto_1fr] gap-x-4 px-1 pb-2 border-b border-border/50">
                    <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
                      User
                    </span>
                    <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide text-right w-12">
                      Trend
                    </span>
                    <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide text-right w-16">
                      Messages
                    </span>
                    <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide text-right w-16">
                      Sessions
                    </span>
                    <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide text-right w-14">
                      Files
                    </span>
                    <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide text-right w-20">
                      Last active
                    </span>
                    <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide text-right">
                      Sign-in
                    </span>
                  </div>

                  <div className="divide-y divide-border/50">
                    {users
                      .slice()
                      .sort((a, b) => b.messages - a.messages)
                      .map((u, idx) => {
                        const displayName =
                          u.firstName && u.lastName
                            ? `${u.firstName} ${u.lastName}`
                            : (u.firstName ?? u.email);
                        const sparkColor = getUserColor(idx);

                        return (
                          <div
                            key={u.userId}
                            className="grid grid-cols-[1.4fr_48px_auto_auto_auto_auto_1fr] gap-x-4 items-center py-2.5 px-1 rounded-lg transition-colors hover:bg-muted/30 cursor-pointer"
                            onClick={() => onSelectUser?.(u.userId)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={e => e.key === 'Enter' && onSelectUser?.(u.userId)}
                          >
                            {/* User info */}
                            <div className="flex items-center gap-2.5 min-w-0">
                              <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[10px] font-semibold select-none">
                                {getInitials(u.firstName, u.lastName, u.email)}
                              </div>
                              <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-1.5">
                                  <p
                                    className="text-sm font-medium text-foreground truncate leading-tight"
                                    title={displayName}
                                  >
                                    {displayName}
                                  </p>
                                  <ActivityStatusBadge usage={u} />
                                  {u.emailVerified === false && (
                                    <span title="Email not verified">
                                      <ShieldAlert
                                        size={11}
                                        className="text-amber-500 flex-shrink-0"
                                      />
                                    </span>
                                  )}
                                </div>
                                <p
                                  className="text-[11px] text-muted-foreground truncate"
                                  title={u.email}
                                >
                                  {u.email}
                                </p>
                              </div>
                            </div>

                            {/* Sparkline */}
                            <div className="flex items-center justify-end w-12">
                              <UserSparkline
                                userId={u.userId}
                                metric="messages"
                                color={sparkColor}
                              />
                            </div>

                            {/* Stats — zeros are muted to keep active users scannable */}
                            <span
                              className={cn(
                                'text-sm tabular-nums text-right w-16',
                                numTone(u.messages)
                              )}
                            >
                              {u.messages.toLocaleString()}
                            </span>
                            <span
                              className={cn(
                                'text-sm tabular-nums text-right w-16',
                                numTone(u.sessions)
                              )}
                            >
                              {u.sessions.toLocaleString()}
                            </span>
                            <span
                              className={cn(
                                'text-sm tabular-nums text-right w-14',
                                numTone(u.files)
                              )}
                            >
                              {u.files.toLocaleString()}
                            </span>

                            {/* Last active — relative time, exact date on hover */}
                            <span
                              className="text-xs text-muted-foreground text-right w-20 whitespace-nowrap"
                              {...(u.lastActive ? { title: formatDate(u.lastActive) } : {})}
                            >
                              {formatRelative(u.lastActive)}
                            </span>

                            {/* Last sign-in from WorkOS (different from last activity) */}
                            <span
                              className="text-xs text-muted-foreground text-right whitespace-nowrap truncate"
                              {...(u.lastSignInAt ? { title: formatDate(u.lastSignInAt) } : {})}
                            >
                              {u.lastSignInAt ? formatRelative(u.lastSignInAt) : '—'}
                            </span>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <EmptyState
              icon={<Activity size={28} />}
              title="No team members to show"
              hint="Add teammates from the Team tab to start tracking per-user usage."
              {...(onGoToTeam
                ? {
                    action: (
                      <Button variant="primary" size="sm" onClick={onGoToTeam}>
                        <Plus size={13} className="mr-1.5" />
                        Go to Team
                      </Button>
                    ),
                  }
                : {})}
            />
          )}
        </InfoSection>
      )}

      {!isLoading && !isError && hasAnyActivity && (
        <p className="text-[11px] text-muted-foreground/60 text-center">
          Counts messages, sessions, and files across your organization.
        </p>
      )}
    </div>
  );
};

// ─── Main AdminPanel ──────────────────────────────────────────────────────────

export const AdminPanel = () => {
  const [activeTab, setActiveTab] = React.useState<AdminTab>('team');
  const [selectedUserId, setSelectedUserId] = React.useState<string | null>(null);

  const tabs: { id: AdminTab; label: string; icon: React.ReactNode }[] = [
    { id: 'team', label: 'Team', icon: <Users size={14} /> },
    { id: 'usage', label: 'Usage', icon: <Activity size={14} /> },
  ];

  return (
    <div className="space-y-6">
      {/* Dashboard header */}
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-0.5">
          <h2 className="text-lg font-semibold text-foreground">Organization</h2>
          <p className="text-sm text-muted-foreground">
            Manage your team, seats, and product usage.
          </p>
        </div>
      </div>

      {/* Organization summary row */}
      <OrgSummaryRow />

      {/* Org health / adoption insights */}
      <OrgHealthPanel />

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-border">
        {tabs.map(tab => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            aria-pressed={activeTab === tab.id}
            className={cn(
              'flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px rounded-t-md',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
              activeTab === tab.id
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/30'
            )}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === 'team' && (
        <TeamTab onViewUsage={() => setActiveTab('usage')} onSelectUser={setSelectedUserId} />
      )}
      {activeTab === 'usage' && (
        <UsageTab onGoToTeam={() => setActiveTab('team')} onSelectUser={setSelectedUserId} />
      )}

      {/* Per-user detail modal */}
      <UserDetailModal userId={selectedUserId} onClose={() => setSelectedUserId(null)} />
    </div>
  );
};

AdminPanel.displayName = 'AdminPanel';
